import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  Puzzle, Download, Trash2, ToggleLeft, ToggleRight,
  BarChart3, Image, Calendar, TrendingUp, Languages,
  Cloud, Split, ShieldCheck, Check
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  "bar-chart": BarChart3, "image": Image, "calendar": Calendar,
  "trending-up": TrendingUp, "languages": Languages, "cloud": Cloud,
  "split": Split, "shield-check": ShieldCheck,
};

const typeColor = {
  seo: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  content: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
  automation: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  analytics: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  system: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  optimization: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400",
};

export default function Extensions() {
  const { t } = useLanguage();
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/extensions`);
      setExtensions(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleInstall = async (extId) => {
    try {
      await axios.post(`${API}/extensions/install`, { extension_id: extId });
      toast.success(t("ext.installed"));
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t("common.error")); }
  };

  const handleToggle = async (extId) => {
    try {
      await axios.post(`${API}/extensions/${extId}/toggle`);
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleUninstall = async (extId) => {
    try {
      await axios.delete(`${API}/extensions/${extId}`);
      toast.success(t("ext.uninstalled"));
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  const installed = extensions.filter(e => e.installed);
  const available = extensions.filter(e => !e.installed);

  return (
    <div data-testid="extensions-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">{t("ext.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1 font-body">{t("ext.subtitle")}</p>
      </div>

      <div className="flex gap-3">
        <Badge variant="secondary" className="text-xs rounded-md">{extensions.length} {t("ext.total")}</Badge>
        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-xs border-0 rounded-md">{installed.length} {t("ext.installedCount")}</Badge>
      </div>

      {/* Zainstalowane */}
      {installed.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-heading font-semibold text-foreground">{t("ext.installedSection")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {installed.map((ext) => {
              const Icon = iconMap[ext.icon] || Puzzle;
              return (
                <Card key={ext.id} data-testid={`ext-${ext.id}`} className="rounded-xl border shadow-sm bg-card border-emerald-200 dark:border-emerald-900">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon className="w-4 h-4 text-primary" strokeWidth={1.5} />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground">{ext.name}</h3>
                          <p className="text-[10px] text-muted-foreground">v{ext.version}</p>
                        </div>
                      </div>
                      <Switch checked={ext.active} onCheckedChange={() => handleToggle(ext.id)} />
                    </div>
                    <p className="text-[11px] text-muted-foreground mb-3">{ext.description}</p>
                    <div className="flex items-center gap-1.5 flex-wrap mb-2">
                      <Badge className={`text-[8px] border-0 rounded-md ${typeColor[ext.type] || typeColor.system}`}>{ext.type}</Badge>
                      {ext.features?.slice(0, 2).map((f, i) => (
                        <Badge key={i} variant="outline" className="text-[8px] rounded-md">{f}</Badge>
                      ))}
                    </div>
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] text-destructive w-full"
                      onClick={() => handleUninstall(ext.id)}>
                      <Trash2 className="w-2.5 h-2.5 mr-1" /> {t("ext.uninstall")}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Dostepne */}
      <div className="space-y-3">
        <h2 className="text-sm font-heading font-semibold text-foreground">{t("ext.availableSection")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {available.map((ext) => {
            const Icon = iconMap[ext.icon] || Puzzle;
            return (
              <Card key={ext.id} data-testid={`ext-${ext.id}`} className="rounded-xl border shadow-sm bg-card hover:shadow-md transition-all">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-muted/50 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground">{ext.name}</h3>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-[9px] text-muted-foreground">v{ext.version}</span>
                        <Badge className={`text-[8px] border-0 rounded-md ${ext.price === "Free" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          {ext.price}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-3 line-clamp-2">{ext.description}</p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {ext.features?.map((f, i) => (
                      <Badge key={i} variant="outline" className="text-[8px] rounded-md">{f}</Badge>
                    ))}
                  </div>
                  <Button size="sm" className="w-full h-8 text-xs gap-1 rounded-lg"
                    data-testid={`install-${ext.id}`} onClick={() => handleInstall(ext.id)}>
                    <Download className="w-3 h-3" strokeWidth={1.5} /> {t("ext.install")}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
